import { ResponseError, type ValidationError } from 'errors';
import type { JSONValue } from 'types';
import { is } from 'utils/helpers';
import { type Result, toResult } from 'utils/result';

import { getHeaderValue, mergeHeaders } from './headers';

const JSON_MIME_TYPE = 'application/json';

export type FetchInput = string | URL;

export type FetchError = Error | ResponseError | ValidationError;

export interface RequestOptions extends Omit<RequestInit, 'body'> {
	/** Resolve an ok response with no data instead of failing. */
	allowEmpty?: boolean;
	/** The request body. JSON-valid values will be stringified. */
	body?: BodyInit | JSONValue;
}

/**
 * Options for a request that resolves an ok response with no data.
 *
 * `allowEmpty` has to be a literal `true` for the wrappers to type their data
 * as possibly undefined, so widen an options variable with `as const`.
 */
export type AllowEmptyOptions<T extends RequestOptions = RequestOptions> = T & {
	allowEmpty: true;
};

/** Options for a request that fails on an ok response with no data. */
export type DisallowEmptyOptions<T extends RequestOptions = RequestOptions> =
	T & {
		allowEmpty?: false;
	};

/**
 * Options for a request that resolves the full response.
 *
 * An empty response is not a failure at this level, so `allowEmpty` has no
 * meaning here.
 */
export type ResponseOptions<T extends RequestOptions = RequestOptions> = Omit<
	T,
	'allowEmpty'
>;

/** A completed request, where a non-ok status is data rather than an error. */
export interface FetchResponse<T> {
	data: T | undefined;
	headers: Headers;
	ok: boolean;
	status: number;
	statusText: string;
}

/** Check if a request or response has a JSON content type. */
function isJson(headers: HeadersInit): boolean {
	const contentType = getHeaderValue(headers, 'Content-Type');
	// Use includes since Content-Type can contain things like charset as well.
	return is.string(contentType) && contentType.includes(JSON_MIME_TYPE);
}

/** Parse a response body, resolving empty values as undefined. */
function parseBody(text: string, headers: Headers): JSONValue | undefined {
	if (!text) {
		return undefined;
	}
	return isJson(headers) ? (JSON.parse(text) as JSONValue) : text;
}

/** Coerce a parsed body to the value callers expect. */
function coerceBody(data: JSONValue | undefined, status: number) {
	// HTTP 204 is No Content. Also resolve any empty values as undefined.
	if (status === 204 || is.nonZeroFalsy(data)) {
		return undefined;
	}
	// Handle exactly zero string to not match the leading zero check below.
	if (data === '0') {
		return 0;
	}
	// Exclude numbers with leading zero since casting it will result in
	// something different (e.g. '01' !== 1).
	if (is.numeric(data) && !String(data).startsWith('0')) {
		return Number(data);
	}
	return data;
}

/** Prepare a request body, stringifying JSON-valid values. */
function prepareBody(
	bodyInput: BodyInit | JSONValue | null | undefined,
): BodyInit | undefined {
	if (!is.defined(bodyInput)) {
		return undefined;
	}
	// Exclude string to not double stringify.
	const isJsonValue =
		is.plainObject(bodyInput) ||
		is.array(bodyInput) ||
		is.bool(bodyInput) ||
		is.number(bodyInput);
	return isJsonValue ? JSON.stringify(bodyInput) : (bodyInput as BodyInit);
}

/** Build a ResponseError from a response that failed. */
export function toResponseError<T>(
	{ data, headers, status, statusText }: FetchResponse<T>,
	message = 'Unknown error',
): ResponseError {
	return new ResponseError({
		headers,
		message,
		response: data,
		status,
		statusText,
	});
}

/**
 * Do a request and get the parsed response, status and headers.
 *
 * A non-ok status resolves rather than throws, so callers can branch on it.
 * Only network and parse failures throw.
 */
export async function fetchResponse<T = unknown>(
	input: FetchInput,
	options?: ResponseOptions,
): Promise<FetchResponse<T>> {
	const headers = new Headers(
		mergeHeaders({ 'content-type': JSON_MIME_TYPE }, options?.headers),
	);

	// When sending form data, the browser itself has to set the Content-Type
	// header for a proper boundary to be included.
	// https://stackoverflow.com/q/39280438
	if (is.instance(options?.body, FormData)) {
		headers.delete('content-type');
	}

	const response = await fetch(input, {
		...options,
		body: prepareBody(options?.body),
		headers,
	});

	// response.json() will throw a parse error for empty responses so always
	// read as text and manually parse to JSON to handle it.
	const text = await response.text();
	const data = coerceBody(parseBody(text, response.headers), response.status);

	return {
		data: data as T | undefined,
		headers: response.headers,
		ok: response.ok,
		status: response.status,
		statusText: response.statusText,
	};
}

/**
 * Get the data from a completed response, failing on a non-ok or empty one.
 *
 * An ok response with no data counts as a failure unless `allowEmpty` is set,
 * so callers get `T` rather than `T | undefined`.
 */
export function unwrapResponse<T>(
	response: FetchResponse<T>,
	allowEmpty: boolean | undefined,
): T | undefined {
	if (!response.ok) {
		throw toResponseError(response);
	}
	if (!allowEmpty && !is.defined(response.data)) {
		throw toResponseError(response, 'Response has no data');
	}
	return response.data;
}

/** Do a request and get the parsed response, throwing on a non-ok or empty response. */
export async function fetchData<T = unknown>(
	input: FetchInput,
	options: AllowEmptyOptions,
): Promise<T | undefined>;
export async function fetchData<T = unknown>(
	input: FetchInput,
	options?: DisallowEmptyOptions,
): Promise<T>;
export async function fetchData<T = unknown>(
	input: FetchInput,
	options?: RequestOptions,
): Promise<T | undefined>;
export async function fetchData<T = unknown>(
	input: FetchInput,
	options?: RequestOptions,
): Promise<T | undefined> {
	const { allowEmpty, ...requestOptions } = options ?? {};
	return unwrapResponse(
		await fetchResponse<T>(input, requestOptions),
		allowEmpty,
	);
}

/** Do a request and get a result type with the data or error. */
export async function fetchResult<T = unknown>(
	input: FetchInput,
	options: AllowEmptyOptions,
): Promise<Result<T | undefined, FetchError>>;
export async function fetchResult<T = unknown>(
	input: FetchInput,
	options?: DisallowEmptyOptions,
): Promise<Result<T, FetchError>>;
export async function fetchResult<T = unknown>(
	input: FetchInput,
	options?: RequestOptions,
): Promise<Result<T | undefined, FetchError>>;
export async function fetchResult<T = unknown>(
	input: FetchInput,
	options?: RequestOptions,
): Promise<Result<T | undefined, FetchError>> {
	return toResult(fetchData<T>(input, options));
}
